// Shared visual language for the floating UI panels + dials. Everything
// in immersive VR is a canvas texture (no DOM in the headset), so this is
// the single place the frosted-glass "look" is defined: glass slabs,
// segmented-control buttons, and consistent typography.

import * as THREE from "three";

// SF Pro on Vision Pro / macOS via system-ui; graceful fallback elsewhere.
const UI_FONT_STACK = "system-ui, -apple-system, sans-serif";

// Build a canvas font string, e.g. glassFont("600 60px").
export function glassFont(weightAndSize: string): string {
  return `${weightAndSize} ${UI_FONT_STACK}`;
}

// Text colors.
export const TEXT_LIGHT = "rgba(236, 239, 246, 0.96)";
export const TEXT_DIM = "rgba(176, 186, 204, 0.88)";
export const TEXT_ON_ACCENT = "rgba(22, 24, 30, 0.96)"; // on a bright pill

export const PANEL_RADIUS = 64;
export const BTN_RADIUS = 44;

// Standard panel texture settings: decode the canvas as sRGB (fuller,
// less-washed color) and use anisotropic filtering so panels stay crisp
// at the glancing angles common in VR.
export function setupPanelTexture(texture: THREE.Texture): void {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 16;
}

// Rounded-rect path (no fill/stroke); avoids relying on ctx.roundRect.
export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Clear the canvas and paint a frosted-glass slab filling it (everything
// outside the rounded path stays transparent, so the plane reads as a
// rounded glass slab over the scene). Dark translucent fill keeps light
// text legible over both the immersive skybox and bright passthrough.
export function paintGlassPanel(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  radius: number = PANEL_RADIUS,
): void {
  ctx.clearRect(0, 0, w, h);
  const inset = 6;
  roundRectPath(ctx, inset, inset, w - 2 * inset, h - 2 * inset, radius);
  // Opaque fill — without real backdrop blur a translucent slab is hard to
  // read over the scene / passthrough. The rounded corners stay transparent
  // (cleared above + clipped to the path), so the panel keeps its shape.
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, "rgb(52, 55, 66)");
  bg.addColorStop(1, "rgb(28, 29, 38)");
  ctx.fillStyle = bg;
  ctx.fill();
  // Soft top sheen (clipped to the panel) for a glassy highlight.
  ctx.save();
  ctx.clip();
  const sheen = ctx.createLinearGradient(0, inset, 0, inset + 160);
  sheen.addColorStop(0, "rgba(255, 255, 255, 0.12)");
  sheen.addColorStop(1, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(0, inset, w, 160);
  ctx.restore();
  // Hairline rim light.
  ctx.lineWidth = 3;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.stroke();
}

// Mode-highlight fill (the Sketch / Place family buttons). A saturated
// blue pill, distinct from the white "active subtool" pill, so the user
// reads them as a different tier — a family stays lit in blue while one of
// its subtools lights up white underneath.
const MODE_ACTIVE_FILL = "rgba(120, 178, 255, 0.97)";

export interface GlassButtonOpts {
  label?: string;
  active?: boolean;
  // Family-level highlight (Sketch / Place): a colored pill, distinct from
  // the white `active` subtool pill. Both can be true on different buttons
  // at once (family blue + subtool white).
  modeActive?: boolean;
  hot?: boolean;
  radius?: number;
  fontPx?: number;
}

// Segmented-control button: active/on → bright pill (dark label);
// modeActive → blue family pill (dark label); hover → brighter glass;
// idle → faint glass. Pass `label` to draw centered text, or omit it and
// draw custom content over the filled shape afterward.
export function paintGlassButton(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: GlassButtonOpts,
): void {
  roundRectPath(ctx, x, y, w, h, opts.radius ?? BTN_RADIUS);
  // Solid (opaque) button surfaces — without real backdrop blur a
  // translucent fill has unreliable contrast over whatever's behind the
  // glass. active → bright pill; modeActive → blue pill; hover → lighter
  // chip; idle → solid chip.
  const pill = opts.active || opts.modeActive;
  ctx.fillStyle = opts.modeActive
    ? MODE_ACTIVE_FILL
    : opts.active
      ? "rgba(245, 247, 252, 0.98)"
      : opts.hot
        ? "rgb(100, 105, 122)"
        : "rgb(74, 78, 92)";
  ctx.fill();
  if (!pill) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
    ctx.stroke();
  }
  if (opts.label) {
    ctx.font = glassFont(`600 ${opts.fontPx ?? 60}px`);
    ctx.fillStyle = pill ? TEXT_ON_ACCENT : TEXT_LIGHT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(opts.label, x + w / 2, y + h / 2 + 2);
  }
}

// Circular glass close button with an X glyph. Used by panels that
// persist until dismissed (settings, help).
export function paintGlassCloseButton(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  hot: boolean,
): void {
  paintGlassButton(ctx, x, y, w, h, { hot, radius: Math.min(w, h) / 2 });
  const pad = w * 0.32;
  ctx.lineWidth = 7;
  ctx.lineCap = "round";
  ctx.strokeStyle = TEXT_LIGHT;
  ctx.beginPath();
  ctx.moveTo(x + pad, y + pad);
  ctx.lineTo(x + w - pad, y + h - pad);
  ctx.moveTo(x + w - pad, y + pad);
  ctx.lineTo(x + pad, y + h - pad);
  ctx.stroke();
  ctx.lineCap = "butt";
}
